/**
 * Boot sequence — terminal-styled transition between splash and catalog.
 *
 * Frames the catalog as the rendered output of the agent pipeline.
 * After the user clicks "Begin", we show a brief simulated terminal
 * session: the orchestrator dispatching polecats, each one logging its
 * stage, and a final "ready" prompt that resolves the promise. Then
 * the timeline takes over and the catalog reveals normally.
 *
 * Pure presentation — no actual subprocess is run. The lines are baked
 * to match the real orchestrator's log output as closely as possible.
 */

// Each line: { text, delayBefore, typeSpeed, color?: string }
// All times in ms. typeSpeed is ms per character; 0 = render instant.
const BOOT_LINES = [
  // ── HERMES block-letter logo ──────────────────────────────────
  { text: "  ██   ██ ███████ ██████  ███    ███ ███████ ███████", delayBefore: 80, typeSpeed: 0, color: "var(--boot-fg)" },
  { text: "  ██   ██ ██      ██   ██ ████  ████ ██      ██     ", delayBefore: 50, typeSpeed: 0, color: "var(--boot-fg)" },
  { text: "  ███████ █████   ██████  ██ ████ ██ █████   ███████", delayBefore: 50, typeSpeed: 0, color: "var(--boot-fg)" },
  { text: "  ██   ██ ██      ██   ██ ██  ██  ██ ██           ██", delayBefore: 50, typeSpeed: 0, color: "var(--boot-fg)" },
  { text: "  ██   ██ ███████ ██   ██ ██      ██ ███████ ███████", delayBefore: 50, typeSpeed: 0, color: "var(--boot-fg)" },
  { text: "", delayBefore: 100 },
  { text: "        a g e n t  ·  nous research  ·  2026-05-03", delayBefore: 80, typeSpeed: 0, color: "var(--boot-dim)" },
  { text: "", delayBefore: 600 },

  { text: "$ gastown run bestiary", delayBefore: 100, typeSpeed: 22, color: "var(--boot-accent)" },
  { text: "", delayBefore: 250 },
  { text: "[gastown]    loading skill: hermes-skill/bestiary.skill.yaml", delayBefore: 80, typeSpeed: 4 },
  { text: "[gastown]    invoking: python pipeline/orchestrator.py", delayBefore: 120, typeSpeed: 4 },
  { text: "", delayBefore: 200 },
  { text: "[mayor]      reading content/script.md (10 entries)", delayBefore: 80, typeSpeed: 5 },
  { text: "[mayor]      dispatching polecats: writer, illustrator, animator", delayBefore: 100, typeSpeed: 4 },
  { text: "", delayBefore: 280 },

  // ── KIMI K2 block-letter logo ─────────────────────────────────
  { text: "             ██   ██ ██ ███    ███ ██", delayBefore: 80, typeSpeed: 0, color: "var(--boot-kimi)" },
  { text: "             ██  ██  ██ ████  ████ ██", delayBefore: 50, typeSpeed: 0, color: "var(--boot-kimi)" },
  { text: "             █████   ██ ██ ████ ██ ██", delayBefore: 50, typeSpeed: 0, color: "var(--boot-kimi)" },
  { text: "             ██  ██  ██ ██  ██  ██ ██", delayBefore: 50, typeSpeed: 0, color: "var(--boot-kimi)" },
  { text: "             ██   ██ ██ ██      ██ ██", delayBefore: 50, typeSpeed: 0, color: "var(--boot-kimi)" },
  { text: "", delayBefore: 80 },
  { text: "                k 2  ·  moonshot ai", delayBefore: 80, typeSpeed: 0, color: "var(--boot-dim)" },
  { text: "", delayBefore: 400 },

  { text: "[writer]     ☾ kimi-k2  voice pass on 10 entries", delayBefore: 80, typeSpeed: 5, color: "var(--boot-kimi)" },
  { text: "[writer]     refined                          ✓", delayBefore: 350, typeSpeed: 5, color: "var(--boot-ok)" },
  { text: "", delayBefore: 100 },

  { text: "[illustrator] flux-1.1-pro: generating paintings", delayBefore: 80, typeSpeed: 5 },
  { text: "[illustrator]   I    the_owl_glass             ✓", delayBefore: 220, typeSpeed: 4, color: "var(--boot-ok)" },
  { text: "[illustrator]   II   the_tooth_puller          ✓", delayBefore: 180, typeSpeed: 4, color: "var(--boot-ok)" },
  { text: "[illustrator]   III  the_conjurer              ✓", delayBefore: 180, typeSpeed: 4, color: "var(--boot-ok)" },
  { text: "[illustrator]   ... 7 more                     ✓", delayBefore: 200, typeSpeed: 4, color: "var(--boot-ok)" },
  { text: "[illustrator] luminance → ascii @ 600×180 grid", delayBefore: 200, typeSpeed: 4 },
  { text: "", delayBefore: 100 },

  { text: "[animator]   ☾ kimi-k2  proposing choreographies", delayBefore: 80, typeSpeed: 5, color: "var(--boot-kimi)" },
  { text: "[animator]   10 scripts generated              ✓", delayBefore: 320, typeSpeed: 4, color: "var(--boot-ok)" },
  { text: "", delayBefore: 100 },

  { text: "[integrator] writing public/manifest/manifest.json", delayBefore: 80, typeSpeed: 4 },
  { text: "", delayBefore: 200 },
  { text: "[mayor]      pipeline complete", delayBefore: 100, typeSpeed: 5 },
  { text: "[gastown]    skill ready · stage handoff", delayBefore: 200, typeSpeed: 5, color: "var(--boot-accent)" },
  { text: "", delayBefore: 700 },

  // ── BESTIARY block-letter title card ─────────────────────────
  // The final beat before the catalog reveals — frames the
  // experience as the bestiary itself coming online. Instant
  // line-by-line render mirrors the HERMES + KIMI logos earlier.
  // Color is the new --boot-system phosphor green — third in the
  // amber → pink → green triptych. Same green is reprised in the
  // closing's [stage]/[mayor]/[gastown]/[hermes] orchestration log
  // to bookend the experience.
  { text: "  ██████  ███████ ███████ ████████  ██   █████  ██████  ██   ██", delayBefore: 80, typeSpeed: 0, color: "var(--boot-system)" },
  { text: "  ██   ██ ██      ██         ██     ██  ██   ██ ██   ██  ██ ██ ", delayBefore: 60, typeSpeed: 0, color: "var(--boot-system)" },
  { text: "  ██████  █████   ███████    ██     ██  ███████ ██████    ███  ", delayBefore: 60, typeSpeed: 0, color: "var(--boot-system)" },
  { text: "  ██   ██ ██           ██    ██     ██  ██   ██ ██  ██     █   ", delayBefore: 60, typeSpeed: 0, color: "var(--boot-system)" },
  { text: "  ██████  ███████ ███████    ██     ██  ██   ██ ██   ██    █   ", delayBefore: 60, typeSpeed: 0, color: "var(--boot-system)" },
  { text: "", delayBefore: 280 },
  { text: "          i n i t i a l i z i n g  .  .  .", delayBefore: 100, typeSpeed: 55, color: "var(--boot-system)" },
  { text: "", delayBefore: 1100 },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Keep the latest typed character pinned to the bottom of the
 * scrollable boot-sequence container — terminal-style auto-scroll so
 * the typing tip stays in view on shorter viewports. The user can
 * still manually scroll up to re-read earlier lines; we only force
 * scroll-to-bottom when content is being actively appended.
 */
function scrollToBottom(target) {
  const scroller = target.parentElement || target;
  scroller.scrollTop = scroller.scrollHeight;
}

async function typeLine(line, target) {
  if (line.delayBefore) await sleep(line.delayBefore);
  if (!line.text) {
    target.appendChild(document.createElement("br"));
    scrollToBottom(target);
    return;
  }
  const span = document.createElement("span");
  if (line.color) span.style.color = line.color;
  target.appendChild(span);
  for (const ch of line.text) {
    span.textContent += ch;
    scrollToBottom(target);
    if (line.typeSpeed) await sleep(line.typeSpeed);
  }
  target.appendChild(document.createElement("br"));
  scrollToBottom(target);
}

/**
 * Run the boot sequence inside #boot-sequence. Resolves when the
 * sequence is done and the screen has faded out. Total runtime: ~6s.
 */
export async function runBootSequence() {
  const root = document.getElementById("boot-sequence");
  const out = document.getElementById("boot-output");
  if (!root || !out) return;
  out.textContent = "";

  // Show, fade in
  root.classList.add("active");
  await sleep(50);
  root.classList.add("visible");

  // Stream the lines
  for (const line of BOOT_LINES) {
    await typeLine(line, out);
  }

  // Final cursor blink for a beat — let BESTIARY INITIALIZING sit
  // on screen so it reads as the dramatic title card before the
  // catalog itself takes over.
  await sleep(1200);

  // Fade out
  root.classList.remove("visible");
  await sleep(900);
  root.classList.remove("active");
}
